import { Component, type ErrorInfo, type ReactNode } from "react";

import { tr } from '@/i18n';
import { isStaleChunkError, reloadOnceForNewBuild } from '@/utils/staleBuild';
interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // A page chunk from the previous build (tab opened before a deploy):
    // reload onto the new build instead of showing an error.
    if (isStaleChunkError(error) && reloadOnceForNewBuild()) return;
    console.error("ErrorBoundary caught:", error, info.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      // Reloading onto the new build (see componentDidCatch) — show a neutral
      // message rather than flashing the error card.
      if (isStaleChunkError(this.state.error)) {
        return (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh", padding: "2rem" }}>
            {tr("common.loading", "Loading…")}
          </div>
        );
      }
      if (this.props.fallback) return this.props.fallback;

      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "60vh",
            padding: "2rem",
            textAlign: "center",
            fontFamily: "var(--font-body)",
          }}
        >
          <div
            style={{
              background: "linear-gradient(135deg, #1e1e2e, #2a2a3e)",
              border: "1px solid color-mix(in srgb, var(--status-danger) 30%, transparent)",
              borderRadius: "16px",
              padding: "2.5rem",
              maxWidth: "480px",
              width: "100%",
              boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
            }}
          >
            <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>⚠️</div>
            <h2
              style={{
                color: "var(--status-danger-fg)",
                marginBottom: "0.75rem",
                fontSize: "1.4rem",
                fontWeight: 600,
              }}
            >
              {tr("ui.something_went_wrong", "Something went wrong")}
            </h2>
            <p
              style={{
                color: "#94a3b8",
                marginBottom: "1.5rem",
                fontSize: "0.9rem",
                lineHeight: 1.6,
              }}
            >
              {this.state.error?.message || tr("ui.an_unexpected_error_occurred", "An unexpected error occurred.")}
            </p>
            <button
              onClick={this.handleReset}
              style={{
                background: "linear-gradient(135deg, var(--accent), var(--accent))",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                padding: "0.75rem 2rem",
                cursor: "pointer",
                fontSize: "0.95rem",
                fontWeight: 600,
                transition: "opacity 0.2s",
              }}
              onMouseEnter={(e) =>
                ((e.target as HTMLButtonElement).style.opacity = "0.85")
              }
              onMouseLeave={(e) =>
                ((e.target as HTMLButtonElement).style.opacity = "1")
              }
            >
              {tr("ui.try_again", "Try Again")}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
